import { useMemo, useState } from "react";
import { apiClient, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import { Award, XCircle, MinusCircle, CheckCircle2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { fmtDate } from "@/lib/dateFormat";

const HERO_STYLE = {
  approved:     "bg-gradient-to-br from-emerald-700 to-emerald-600 text-white border-emerald-800 shadow-lg shadow-emerald-900/20",
  denied:       "bg-gradient-to-br from-rose-700 to-rose-600 text-white border-rose-800 shadow-lg shadow-rose-900/20",
  not_selected: "bg-gradient-to-br from-slate-700 to-slate-600 text-white border-slate-800 shadow-lg shadow-slate-900/20",
};

const FINAL_LABEL = { approved: "Approved", denied: "Not Approved", not_selected: "Not Selected" };

const FINAL_SUBLINE = {
  approved:     "This household has been selected and will move forward. All applicants closed and archived together.",
  denied:       "The Property Manager has declined this household. All applicants closed and archived. An Adverse Action Notice may be required per applicant.",
  not_selected: "This household was qualified but not chosen due to availability or another household being selected. Neutral outcome — does NOT imply risk or denial. All applicants closed and archived.",
};

const FINAL_OPTIONS = [
  { value: "approved",     label: "Approved",     blurb: "Selected household — all applicants will move forward.", Icon: Award,        tone: "emerald" },
  { value: "denied",       label: "Not Approved", blurb: "Declined. A reason is required (FCRA).",                  Icon: XCircle,      tone: "rose" },
  { value: "not_selected", label: "Not Selected", blurb: "Qualified but not chosen. Neutral.",                       Icon: MinusCircle,  tone: "slate" },
];

const OPTION_TONE = {
  emerald: { active: "border-emerald-600 ring-2 ring-emerald-500 bg-emerald-50", icon: "text-emerald-700 bg-emerald-100" },
  rose:    { active: "border-rose-600 ring-2 ring-rose-500 bg-rose-50",          icon: "text-rose-700 bg-rose-100" },
  slate:   { active: "border-slate-600 ring-2 ring-slate-500 bg-slate-100",      icon: "text-slate-700 bg-slate-200" },
};

/**
 * HouseholdFinalDecisionPanel
 *
 * Renders the household-level closing-decision control set + post-decision
 * banner for a household profile (GroupDetailPage).
 *
 * Visibility rules:
 *   • Only Property Managers see the action button.
 *   • Once household.final_decision is set ("uniform"), this panel becomes
 *     the closing-outcome banner and the action button is hidden.
 *   • If any member is already individually decided (final_decision_state in
 *     {"mixed","partial"}), this panel shows a blocking notice instead of
 *     the action button — surfacing exactly which applicants are blocking
 *     so the PM can finish them one-by-one in the applicant profile.
 *
 * Props:
 *   group: full group payload from /api/groups/{gid}
 *   isPM:  bool — whether the viewing user is a Property Manager
 *   onApplied: callback after successful bulk close (parent reloads)
 */
export default function HouseholdFinalDecisionPanel({ group, isPM, onApplied }) {
  const household = group.household || {};
  const finalState = household.final_decision_state;     // null | uniform | partial | mixed
  const finalValue = household.final_decision;           // approved | denied | not_selected | null
  const members = group.member_screenings || [];

  // Blocking applicants (already individually decided) when state is mixed
  // or partial. Surfaced so the PM understands why the bulk close action
  // is disabled and which records they need to handle manually.
  const blockers = useMemo(
    () => members.filter((m) => !!m.final_decision),
    [members],
  );

  const [open, setOpen] = useState(false);
  const [choice, setChoice] = useState("approved");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  // ───────── Closed-banner mode (uniform final decision already set) ─────────
  if (finalState === "uniform" && finalValue) {
    const style = HERO_STYLE[finalValue];
    return (
      <div className={`relative overflow-hidden rounded-lg border ${style} p-6 sm:p-8`}
           data-testid="household-final-decision-banner">
        <div className="absolute inset-0 dotted-bg pointer-events-none opacity-15" />
        <div className="relative">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold mb-3 flex items-center gap-1.5">
            <span className="h-1.5 w-1.5 rounded-full bg-white/90" />
            <span className="text-white/90">PM Final Decision · Household</span>
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-none text-white"
              data-testid="household-final-decision-headline">
            {FINAL_LABEL[finalValue]}
          </h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-white/85">{FINAL_SUBLINE[finalValue]}</p>
          {/* Show the reason from the first member (all members share the same value). */}
          {members[0]?.final_decision_reason && (
            <div className="mt-5 max-w-2xl p-4 text-sm bg-white/12 backdrop-blur-sm border-l-2 border-white text-white"
                 data-testid="household-final-decision-reason">
              {members[0].final_decision_reason}
            </div>
          )}
          {members[0]?.final_decision_at && (
            <div className="text-[10px] font-mono mt-4 tracking-[0.12em] uppercase text-white/70">
              Closed across {members.length} applicant{members.length !== 1 ? "s" : ""} · {fmtDate(members[0].final_decision_at)} · Archived
            </div>
          )}
        </div>
      </div>
    );
  }

  // ───────── Blocked mode (some — but not all — members already decided) ─────────
  if (isPM && (finalState === "mixed" || finalState === "partial")) {
    return (
      <div className="rounded-lg border border-amber-300 bg-amber-50 p-5 sm:p-6"
           data-testid="household-final-decision-blocked">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-md bg-amber-100 flex items-center justify-center shrink-0">
            <AlertTriangle className="h-5 w-5 text-amber-700" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-amber-800">Household Close · Blocked</div>
            <h3 className="font-serif text-xl text-slate-900 mt-1">Some applicants are already individually decided</h3>
            <p className="text-sm text-slate-700 mt-2 leading-relaxed">
              To close this household in one action, every applicant must be undecided. Resolve the decided
              applicants one-by-one on their applicant profiles, or close the remaining applicants individually.
            </p>
            <ul className="mt-3 space-y-1 text-sm" data-testid="household-final-decision-blockers">
              {blockers.map((b) => (
                <li key={b.screening_id} className="flex items-center gap-2">
                  <span className="font-mono text-[10px] tracking-wider uppercase font-bold text-amber-900">
                    {FINAL_LABEL[b.final_decision] || b.final_decision}
                  </span>
                  <span className="text-slate-700">{b.applicant_name}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  }

  // ───────── PM action mode (no members decided yet) ─────────
  if (!isPM) return null;

  const submit = async () => {
    if (choice === "denied" && reason.trim().length < 3) {
      toast.error("A reason (≥3 characters) is required for a Not Approved decision.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await apiClient.post(`/groups/${group.id}/final-decision`, {
        decision: choice,
        reason: reason.trim() || null,
      });
      toast.success(`Household decision recorded: ${FINAL_LABEL[data.decision]}. ${data.applied} applicant${data.applied !== 1 ? "s" : ""} closed & archived.`);
      setOpen(false);
      onApplied?.();
    } catch (e) {
      // 409 returns structured detail.blockers; surface a useful message.
      const detail = e.response?.data?.detail;
      if (detail?.blockers) {
        toast.error(detail.message || "Household has applicants already decided.");
      } else {
        toast.error(formatApiError(detail) || "Could not record household decision.");
      }
    } finally { setBusy(false); }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 sm:p-6 shadow-sm"
         data-testid="household-final-decision-action">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500">PM Final Decision · Household</div>
          <h3 className="font-serif text-xl text-slate-900 mt-1">Close this household</h3>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed max-w-xl">
            Apply one closing outcome to all {members.length} applicant{members.length !== 1 ? "s" : ""} in this household.
            The household will move out of active queues and into the Closed Applications Archive.
          </p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#0A192F] hover:bg-[#0A192F]/90 text-white rounded-md h-11 px-5"
                    data-testid="open-household-final-decision">
              <CheckCircle2 className="h-4 w-4 mr-2" /> Record Household Decision
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-serif text-2xl">Close household with one decision</DialogTitle>
              <DialogDescription>
                Applies to all {members.length} applicant{members.length !== 1 ? "s" : ""} in this household. Each applicant keeps their own record. Archives the household.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              {FINAL_OPTIONS.map((opt) => {
                const active = choice === opt.value;
                const tone = OPTION_TONE[opt.tone];
                const Icon = opt.Icon;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setChoice(opt.value)}
                    data-testid={`household-final-option-${opt.value}`}
                    className={`w-full text-left rounded-md border p-3 flex items-start gap-3 transition-all ${
                      active ? tone.active : "border-slate-200 hover:border-slate-300 bg-white"
                    }`}
                  >
                    <div className={`h-9 w-9 rounded-md flex items-center justify-center shrink-0 ${active ? tone.icon : "bg-slate-100 text-slate-500"}`}>
                      <Icon className="h-5 w-5" strokeWidth={2} />
                    </div>
                    <div className="min-w-0">
                      <div className="font-semibold text-slate-900">{opt.label}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{opt.blurb}</div>
                    </div>
                  </button>
                );
              })}
              <div>
                <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#4A5568]">
                  Reason {choice === "denied" ? <span className="text-rose-600">· required</span> : <span className="text-slate-400">· optional</span>}
                </Label>
                <Textarea
                  rows={3}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={choice === "denied"
                    ? "FCRA-style reason for declining the household. Will be applied to every applicant."
                    : "Optional context — applied to every applicant in the household."}
                  className="mt-1.5 rounded-sm"
                  data-testid="household-final-decision-reason-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={submit}
                disabled={busy}
                className="bg-[#0A192F] hover:bg-[#0A192F]/90 rounded-sm"
                data-testid="household-final-decision-submit"
              >
                {busy ? "Recording…" : `Close all ${members.length} & archive`}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
